import { ReactNode, useRef } from 'react';
import { BadgeV3, TooltipV2 } from '@cimplico/components';
import { calcRelativeTime, formatDateTime } from '../../../utilities/formatting';
import { CheckCircleIcon, ExclamationCircleIcon, XCircleIcon } from '@heroicons/react/20/solid';
import { WorkpaperProcedure, WorkpaperProcedureStatusEnum } from '../../../types/workpaper-procedure';
import WorkpaperProcedureTypeBadge from '../WorkpaperProcedureTypeBadge';
import UserAvatar from '../../../components/avatar/UserAvatar';
import WorkpaperProcedureRoleBadge from '../WorkpaperProcedureRoleBadge';
import useIsClamped from '../../../hooks/use-line-clamp-active';

export default function WorkpaperProcedureDetails({ workpaperProcedure }: { workpaperProcedure: WorkpaperProcedure }): JSX.Element {
    const reasonRef = useRef(null);
    const isReasonClamped = useIsClamped(reasonRef);
    const wpUserAction = workpaperProcedure?.userAction;

    return (
        <div className="flex flex-col gap-4">
            <div>
                <div className="flex gap-1 text-xs">
                    {workpaperProcedure.workpaperProcedureCategory && (
                        <p className="text-gray-500">{workpaperProcedure.workpaperProcedureCategory.name} / </p>
                    )}
                    <p className="text-gray-800">{workpaperProcedure.reference} </p>
                </div>
                <p className="text-lg text-gray-800 font-medium">{workpaperProcedure.name}</p>
            </div>
            <p className="text-sm text-gray-800 whitespace-pre-line">{workpaperProcedure.description}</p>
            <div className="flex flex-col gap-2">
                <WorkpaperProcedureDetailRow label="Status">
                    {workpaperProcedure.status === 'ACTIVE' && (
                        <BadgeV3 leftIcon={<ExclamationCircleIcon />} size="small" label="Not completed" rounded />
                    )}
                    {workpaperProcedure.status === 'CHECKED' && (
                        <BadgeV3 leftIcon={<CheckCircleIcon />} size="small" label="Checked" color="green" rounded />
                    )}
                    {workpaperProcedure.status === 'NOT_APPLICABLE' && (
                        <BadgeV3 leftIcon={<XCircleIcon />} size="small" label="Not applicable" color="red" rounded />
                    )}
                </WorkpaperProcedureDetailRow>

                {workpaperProcedure.status === WorkpaperProcedureStatusEnum.NOT_APPLICABLE && (
                    <WorkpaperProcedureDetailRow label="Reason">
                        <TooltipV2 message={workpaperProcedure.notApplicableReason} disabled={!isReasonClamped}>
                            <p ref={reasonRef} className="line-clamp-2 whitespace-normal">
                                {workpaperProcedure.notApplicableReason ? workpaperProcedure.notApplicableReason : '-'}
                            </p>
                        </TooltipV2>
                    </WorkpaperProcedureDetailRow>
                )}

                <WorkpaperProcedureDetailRow label="Labels">
                    <WorkpaperProcedureRoleBadge role={workpaperProcedure.role} />
                    {workpaperProcedure.entityType && <WorkpaperProcedureTypeBadge entityType={workpaperProcedure.entityType} />}
                </WorkpaperProcedureDetailRow>

                <WorkpaperProcedureDetailRow label="Completed by">
                    {wpUserAction ? (
                        <>
                            <UserAvatar size="mini" user={wpUserAction} />
                            {wpUserAction.firstName} {wpUserAction.lastName}
                        </>
                    ) : (
                        '-'
                    )}
                </WorkpaperProcedureDetailRow>

                <WorkpaperProcedureDetailRow label="Completed date">
                    <TooltipV2 disabled={!wpUserAction} message={wpUserAction && formatDateTime(wpUserAction.date)}>
                        {wpUserAction ? calcRelativeTime(wpUserAction.date) : '-'}
                    </TooltipV2>
                </WorkpaperProcedureDetailRow>
            </div>
        </div>
    );
}

function WorkpaperProcedureDetailRow({ label, children }: { label: string; children: ReactNode | ReactNode[] }): JSX.Element {
    return (
        <div className="flex gap-2 min-h-[24px] items-start">
            <p className="w-[125px] text-sm text-gray-600 flex-shrink-0">{label}</p>
            <div className="flex gap-1 text-sm text-gray-600 grow flex-wrap">{children}</div>
        </div>
    );
}
